<template>
  <div class="page">
    <div class="toolbar">
      <el-input v-model="keyword" placeholder="搜索姓名/手机号/就诊号" clearable class="search" @clear="fetchData" @keyup.enter.native="fetchData" />
      <el-button type="primary" @click="openCreate">创建来访者</el-button>
      <el-button type="success" @click="openImport">批量导入</el-button>
    </div>

    <el-table :data="items" stripe v-loading="loading" class="table">
      <el-table-column prop="visit_number" label="就诊号" width="140" />
      <el-table-column prop="name" label="姓名" width="140" />
      <el-table-column label="性别" width="80">
        <template slot-scope="{ row }">{{ formatGender(row.gender) }}</template>
      </el-table-column>
      <el-table-column prop="age" label="年龄" width="80" />
      <el-table-column prop="phone" label="电话" width="140" />
      <el-table-column prop="created_by_name" label="创建人" width="140" />
      <el-table-column label="操作" min-width="200">
        <template slot-scope="{ row }">
          <el-button size="mini" @click="openCreateAssessment(row)">开具测评</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        layout="prev, pager, next"
        :page-size="pageSize"
        :current-page="page"
        :total="total"
        @current-change="onPageChange"
      />
    </div>

    <el-dialog title="创建来访者" :visible.sync="createVisible" width="520px">
      <el-form :model="createForm" :rules="createRules" ref="createForm" label-width="90px">
        <el-form-item label="姓名" prop="name">
          <el-input v-model="createForm.name" />
        </el-form-item>
        <el-form-item label="性别" prop="gender">
          <el-select v-model="createForm.gender" placeholder="请选择">
            <el-option label="男" value="M" />
            <el-option label="女" value="F" />
            <el-option label="其他" value="O" />
          </el-select>
        </el-form-item>
        <el-form-item label="出生日期" prop="birth_date">
          <el-date-picker v-model="createForm.birth_date" type="date" value-format="yyyy-MM-dd" placeholder="YYYY-MM-DD" />
        </el-form-item>
        <el-form-item label="电话" prop="phone">
          <el-input v-model="createForm.phone" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="creating" @click="submitCreate">创建</el-button>
      </span>
    </el-dialog>

    <el-dialog title="开具测评" :visible.sync="assessmentVisible" width="560px">
      <div class="hint">选择量表后，将生成一个可分享给来访者的测评链接（uid）。</div>
      <el-form :model="assessmentForm" label-width="90px" ref="assessmentForm">
        <el-form-item label="来访者">
          <el-input :value="assessmentForm.visitor_name" disabled />
        </el-form-item>
        <el-form-item label="量表">
          <el-select
            v-model="assessmentForm.scale"
            placeholder="请选择量表"
            filterable
            remote
            :remote-method="searchScales"
            :loading="loadingScales"
            style="width: 100%"
            v-loadmore="loadMoreScales"
            :popper-append-to-body="false"
          >
            <el-option v-for="s in scales" :key="s.id" :label="s.title" :value="s.id" />
            <div v-if="appendingScales" style="text-align: center; color: #999; font-size: 12px; padding: 8px;">加载中...</div>
            <div v-if="noMoreScales && scales.length > 0" style="text-align: center; color: #999; font-size: 12px; padding: 8px;">没有更多了</div>
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="assessmentVisible = false">取消</el-button>
        <el-button type="primary" :loading="creatingAssessment" @click="submitAssessment">创建测评</el-button>
      </span>
    </el-dialog>

    <el-dialog title="批量导入来访者" :visible.sync="importDialogVisible" width="500px">
      <div style="margin-bottom: 16px;">
        <el-button size="small" type="text" icon="el-icon-download" @click="downloadTemplate">下载CSV模版</el-button>
        <div style="font-size: 12px; color: #999; margin-top: 4px;">请使用UTF-8编码的CSV文件，或Excel文件(.xlsx)。</div>
      </div>
      
      <el-upload
        class="upload-demo"
        drag
        action=""
        :auto-upload="false"
        :on-change="onImportFileChange"
        :file-list="importFileList"
        :limit="1"
        accept=".csv,.xlsx,.xls"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
      </el-upload>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="importing" @click="confirmImport">开始导入</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getVisitors, createVisitor, importVisitors } from '@/api/visitors'
import { getScales } from '@/api/scales'
import { createAssessment } from '@/api/assessments'
import { validatePhone } from '@/utils/validate'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'Visitors',
  directives: {
    loadmore: {
      bind(el, binding) {
        // Find the dropdown wrapper. Since popper-append-to-body is false, it should be inside el.
        // Structure: el-select -> ... -> el-select-dropdown -> el-scrollbar -> el-select-dropdown__wrap
        const selectDropdown = el.querySelector('.el-select-dropdown .el-select-dropdown__wrap')
        if (selectDropdown) {
          selectDropdown.addEventListener('scroll', function() {
            const condition = this.scrollHeight - this.scrollTop <= this.clientHeight + 10 // 10px tolerance
            if (condition) {
              binding.value()
            }
          })
        }
      }
    }
  },
  data() {
    return {
      loading: false,
      items: [],
      page: 1,
      pageSize: 10,
      total: 0,
      keyword: '',

      createVisible: false,
      creating: false,
      createForm: { name: '', gender: 'M', birth_date: '', phone: '' },
      createRules: {
        name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        gender: [{ required: true, message: '请选择性别', trigger: 'change' }],
        birth_date: [{ required: true, message: '请选择出生日期', trigger: 'change' }],
        phone: [
          { required: true, message: '请输入电话', trigger: 'blur' },
          { validator: validatePhone, trigger: 'blur' }
        ],
      },

      assessmentVisible: false,
      creatingAssessment: false,
      assessmentForm: { visitor: null, visitor_name: '', scale: null },
      scales: [],
      loadingScales: false,
      appendingScales: false,
      scalePage: 1,
      scaleTotal: 0,
      scaleKeyword: '',
      noMoreScales: false,
      debouncedSearchScales: null,

      importDialogVisible: false,
      importing: false,
      importFileList: [],
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    formatGender(v) {
      if (v === 'M') return '男'
      if (v === 'F') return '女'
      if (v === 'O') return '其他'
      return v || ''
    },
    async fetchAllPages(apiFunc, params) {
      const pageSize = 50
      let page = 1
      let items = []
      while (page <= 200) {
        const resp = await apiFunc({ ...(params || {}), page, page_size: pageSize })
        const data = resp.data || {}
        const chunk = data.results || []
        items = items.concat(chunk)
        if (!data.next || chunk.length === 0) break
        page += 1
      }
      return items
    },
    async fetchData() {
      this.loading = true
      try {
        const params = { page: this.page }
        if (this.keyword) params.search = this.keyword
        const resp = await getVisitors(params)
        this.items = resp.data.results || []
        this.total = resp.data.count || 0
      } catch (e) {
        this.items = []
        this.total = 0
        if (e && e.response && e.response.status === 401) return
        notifyApiError(e, '获取来访者列表失败')
      } finally {
        this.loading = false
      }
    },
    onPageChange(p) {
      this.page = p
      this.fetchData()
    },
    openCreate() {
      this.createVisible = true
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.clearValidate())
    },
    submitCreate() {
      this.$refs.createForm.validate(async (valid) => {
        if (!valid) return
        this.creating = true
        try {
          await createVisitor(this.createForm)
          this.$message.success('创建成功')
          this.createVisible = false
          this.page = 1
          this.fetchData()
        } catch (e) {
          notifyApiError(e, '创建失败')
        } finally {
          this.creating = false
        }
      })
    },
    onScaleSelectFocus() {
      if (this.scales.length === 0) {
        this.searchScales('')
      }
    },
    async searchScales(query) {
      this.scaleKeyword = query
      this.scalePage = 1
      this.scales = []
      this.noMoreScales = false
      await this.fetchScalesPage()
    },
    async loadMoreScales() {
      if (this.loadingScales || this.appendingScales || this.noMoreScales) return
      this.scalePage += 1
      await this.fetchScalesPage(true)
    },
    async fetchScalesPage(append = false) {
      if (append) {
        this.appendingScales = true
      } else {
        this.loadingScales = true
      }
      try {
        const params = { page: this.scalePage, page_size: 20 }
        if (this.scaleKeyword) {
          params.search = this.scaleKeyword
        }
        const resp = await getScales(params)
        let results = resp.data.results || []
        results = results.filter(s => s.is_active !== false)
        
        if (!resp.data.next) {
          this.noMoreScales = true
        }

        if (append) {
          this.scales = [...this.scales, ...results]
        } else {
          this.scales = results
        }
      } catch (e) {
        if (!append) this.scales = []
      } finally {
        this.loadingScales = false
        this.appendingScales = false
      }
    },
    openCreateAssessment(visitor) {
      this.assessmentForm = { visitor: visitor.id, visitor_name: visitor.name, scale: null }
      this.assessmentVisible = true
      // Pre-load scales if empty
      if (this.scales.length === 0) {
        this.searchScales('')
      }
    },
    async submitAssessment() {
      if (!this.assessmentForm.scale) {
        this.$message.warning('请选择量表')
        return
      }
      this.creatingAssessment = true
      try {
        const resp = await createAssessment({
          visitor: this.assessmentForm.visitor,
          scale: this.assessmentForm.scale,
        })
        this.$message.success('测评创建成功')
        this.assessmentVisible = false
        this.$router.push(`/app/assessments/${resp.data.id}`)
      } catch (e) {
        notifyApiError(e, '创建测评失败')
      } finally {
        this.creatingAssessment = false
      }
    },
    openImport() {
      this.importDialogVisible = true
      this.importFileList = []
    },
    onImportFileChange(file) {
      this.importFileList = [file]
    },
    downloadTemplate() {
      // CSV Template
      const header = '姓名,性别,出生日期,电话\n'
      const example = '张三,男,1990-01-01,13800138000\n李四,女,1995-05-20,13900139000'
      const blob = new Blob([`\ufeff${header}${example}`], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'visitor_import_template.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    async confirmImport() {
      if (this.importFileList.length === 0) {
        this.$message.warning('请先选择文件')
        return
      }
      
      this.importing = true
      const file = this.importFileList[0].raw
      const formData = new FormData()
      formData.append('file', file)
      
      try {
        const resp = await importVisitors(formData)
        
        if (resp.status === 207) {
          // Partial success
          const msg = resp.data.detail
          const errors = resp.data.errors || []
          // Show errors in notification or alert
          const errorMsg = errors.slice(0, 5).join('\n') + (errors.length > 5 ? '\n...' : '')
          this.$alert(`<div style="white-space: pre-line">${msg}\n\n错误详情:\n${errorMsg}</div>`, '部分导入失败', {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '确定'
          })
        } else {
          this.$message.success(resp.data.detail || '导入成功')
          this.importDialogVisible = false
        }
        this.page = 1
        this.fetchData()
      } catch (e) {
        notifyApiError(e, '导入失败')
      } finally {
        this.importing = false
      }
    }
  },
}
</script>

<style scoped>
.toolbar {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}
.search {
  max-width: 360px;
}
.table {
  background: var(--bg-card);
  border-radius: 12px;
  overflow: hidden;
}
.pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}
.hint {
  margin-bottom: 12px;
  color: var(--text-secondary);
  font-size: 13px;
}
</style>
